/**
 * ComparisonReport component for displaying AI-generated comparison narrative
 * Shows a 200-300 word analysis comparing the selected creatives
 */

import { Check, Copy, FileText } from 'lucide-react';
import { useState } from 'react';

import { Card } from '@/components/ui/Card';
import { cn } from '@/lib/utils';

// ============================================================================
// TYPES
// ============================================================================

export interface ComparisonReportProps {
  report: string;
  creativeCount: number;
  className?: string;
}

// ============================================================================
// COMPARISON REPORT COMPONENT
// ============================================================================

export function ComparisonReport({
  report,
  creativeCount,
  className,
}: ComparisonReportProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(report);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = report;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const wordCount = report.split(/\s+/).length;

  return (
    <Card className={cn('p-6', className)}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-brand/10 rounded-lg">
            <FileText className="h-5 w-5 text-brand" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-primary">
              AI Comparison Report
            </h3>
            <p className="text-sm text-secondary">
              {creativeCount} creatives compared • {wordCount} words
            </p>
          </div>
        </div>
        <button
          onClick={handleCopy}
          className={cn(
            'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
            copied
              ? 'bg-success/10 text-success'
              : 'bg-surface-subtle hover:bg-surface-muted text-secondary'
          )}
          aria-label={copied ? 'Copied' : 'Copy to clipboard'}
        >
          {copied ? (
            <>
              <Check className="h-4 w-4" />
              Copied
            </>
          ) : (
            <>
              <Copy className="h-4 w-4" />
              Copy
            </>
          )}
        </button>
      </div>

      {/* Report Content */}
      <div className="bg-surface-subtle rounded-lg p-4 border border-border">
        <p className="text-primary leading-relaxed whitespace-pre-wrap">
          {report}
        </p>
      </div>

      {/* Footer hint */}
      <p className="mt-3 text-xs text-muted">
        This report is generated by AI and compares the selected creatives based
        on their analysis results. Copy to share via email or Slack.
      </p>
    </Card>
  );
}

export default ComparisonReport;
